import {useCallback, useEffect, useState} from 'react'
import {BASE_URL} from '../../../api'
import config from '../../config'
const {axios} = config

// Roadmap #11 phases 2 & 3 (2026-09-23): class packs and monthly
// memberships that practitioners/businesses sell in the app. Enforma can
// see all of them and remove (stop selling) or restore any. Removing
// doesn't affect clients who already bought one.
const describe = (p) => {
  if (p.kind === 'pack') {
    return `${p.credits} class${p.credits === 1 ? '' : 'es'}${p.validityDays ? `, valid ${p.validityDays} days` : ', no expiry'}`
  }
  const allowance =
    p.classAllowance === 'unlimited'
      ? 'Unlimited classes'
      : p.classAllowance === 'limited'
      ? `${p.classesPerPeriod} classes / month`
      : 'Access only'
  return p.accessDescription ? `${allowance} — ${p.accessDescription}` : allowance
}

const coverage = (p) => {
  if (p.kind === 'membership' && p.classAllowance === 'none') return '—'
  const parts = []
  if (p.allClasses) parts.push('All classes')
  else if ((p.classNames || []).length) parts.push(p.classNames.join(', '))
  if (p.allServices) parts.push('All services')
  else if ((p.services || []).length) parts.push(`${p.services.length} service(s)`)
  return parts.join(' · ') || '—'
}

const Passes = () => {
  const [rows, setRows] = useState([])
  const [kind, setKind] = useState('all')
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [busyId, setBusyId] = useState(null)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const {data} = await axios.get(`${BASE_URL}/admin/passes/products`)
      setRows(data.data || [])
    } catch (e) {
      setError('Could not load packs and memberships.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const act = async (p, action) => {
    if (action === 'remove' && !window.confirm(`Stop ${p.owner?.name} selling "${p.name}"? Clients who already bought it keep it.`)) return
    setBusyId(p._id)
    try {
      await axios.put(`${BASE_URL}/admin/passes/products/${p._id}/${action}`)
      await load()
    } catch (e) {
      setError('Could not update this item.')
    } finally {
      setBusyId(null)
    }
  }

  const q = search.trim().toLowerCase()
  const shown = rows
    .filter((r) => kind === 'all' || r.kind === kind)
    .filter((r) => !q || [r.name, r.owner?.name, r.owner?.email].some((v) => String(v || '').toLowerCase().includes(q)))

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0 pt-6'>
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Packs &amp; Memberships</h3>
        </div>
        <div className='card-toolbar gap-3'>
          <input className='form-control form-control-solid w-250px' placeholder='Search name or practitioner' value={search} onChange={(e) => setSearch(e.target.value)} />
          <select className='form-select form-select-solid w-200px' value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value='all'>All</option>
            <option value='pack'>Class packs</option>
            <option value='membership'>Memberships</option>
          </select>
        </div>
      </div>
      <div className='card-body border-top p-9'>
        <p className='text-muted'>
          Practitioners and businesses create these in the app. Removing one stops it being sold;
          clients who already bought it keep using it.
        </p>
        {error && <div className='alert alert-danger py-3'>{error}</div>}
        <div className='table-responsive'>
          <table className='table align-middle table-row-dashed fs-6 gy-4'>
            <thead>
              <tr className='text-start text-muted fw-bolder fs-7 text-uppercase'>
                <th>Name</th>
                <th>Practitioner / Business</th>
                <th>Type</th>
                <th>Includes</th>
                <th>Can book</th>
                <th>Price</th>
                <th>Active holders</th>
                <th>Status</th>
                <th className='text-end'></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className='text-muted'>Loading...</td></tr>
              )}
              {!loading && shown.length === 0 && (
                <tr><td colSpan={9} className='text-muted'>Nothing found.</td></tr>
              )}
              {!loading &&
                shown.map((p) => (
                  <tr key={p._id}>
                    <td>
                      <div className='fw-bold'>{p.name}</div>
                      {p.description && <div className='text-muted fs-7'>{p.description}</div>}
                    </td>
                    <td>
                      <div>{p.owner?.name}</div>
                      <div className='text-muted fs-7'>{p.owner?.primaryLocation}</div>
                    </td>
                    <td>{p.kind === 'pack' ? 'Class pack' : 'Monthly membership'}</td>
                    <td className='fs-7'>{describe(p)}</td>
                    <td className='fs-7'>{coverage(p)}</td>
                    <td>
                      {p.price} {p.currency}
                      {p.kind === 'membership' ? ' / month' : ''}
                    </td>
                    <td>{p.activeHolders || 0}</td>
                    <td>
                      {p.removedAt ? (
                        <span className='badge badge-light-danger'>Removed by Enforma</span>
                      ) : p.active ? (
                        <span className='badge badge-light-success'>On sale</span>
                      ) : (
                        <span className='badge badge-light-secondary'>Not on sale</span>
                      )}
                    </td>
                    <td className='text-end'>
                      {p.removedAt ? (
                        <button className='btn btn-sm btn-light' disabled={busyId === p._id} onClick={() => act(p, 'restore')}>Restore</button>
                      ) : (
                        <button className='btn btn-sm btn-light-danger' disabled={busyId === p._id} onClick={() => act(p, 'remove')}>Remove</button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default Passes
